import { useMemo, useState, type ReactNode } from 'react';
import {
  DataTable,
  LevelMeter,
  Switch,
  useClientSort,
  type DataTableColumn,
  type DataTableSort,
} from '@wundo/ui';
import type { TrackedPageRoute } from '@wundo/shared/pageVisit.ts';
import type { PageUsageRow } from '@/lib/dashboard.ts';
import { TRACKED_PAGES, type PageSection } from './trackedPages.ts';
import { ChartCard } from './ChartCard.tsx';

interface PageUsageTableRow {
  route: TrackedPageRoute;
  label: string;
  section: PageSection;
  users: number;
  users_last_30_days: number;
  /** Among the people who opened the page; null when nobody did. */
  visits_per_user: number | null;
  total_visits: number;
}

function percentage(part: number, whole: number): string {
  return whole === 0 ? '–' : `${Math.round((part / whole) * 100)}%`;
}

/* Unused pages, when shown, recede so the pages people do use stand out. */
function cell(row: PageUsageTableRow, content: ReactNode): ReactNode {
  return row.users === 0 ? <span className='text-ink-muted'>{content}</span> : content;
}

function columns(
  totalUsers: number,
  showRoutes: boolean,
): DataTableColumn<PageUsageTableRow>[] {
  return [
    {
      key: 'label',
      header: 'Page',
      sortable: true,
      render: (row) => cell(row, row.label),
    },
    {
      key: 'section',
      header: 'Section',
      sortable: true,
      render: (row) => cell(row, row.section),
    },
    ...(showRoutes
      ? [
          {
            key: 'route',
            header: 'Route',
            render: (row: PageUsageTableRow) =>
              cell(row, <code className='text-xs'>{row.route}</code>),
          },
        ]
      : []),
    {
      key: 'users',
      header: 'Used by',
      sortable: true,
      nowrap: true,
      render: (row) => (
        <div className='flex items-center gap-3'>
          <LevelMeter
            value={totalUsers === 0 ? 0 : row.users / totalUsers}
            label={`${row.label} used by`}
            className='w-24'
          />
          {cell(
            row,
            `${percentage(row.users, totalUsers)} · ${row.users} / ${totalUsers}`,
          )}
        </div>
      ),
    },
    {
      key: 'users_last_30_days',
      header: 'Active last 30 days',
      sortable: true,
      align: 'end',
      nowrap: true,
      render: (row) => cell(row, percentage(row.users_last_30_days, totalUsers)),
    },
    {
      key: 'visits_per_user',
      header: 'Visits per user',
      sortable: true,
      align: 'end',
      nowrap: true,
      render: (row) =>
        cell(row, row.visits_per_user === null ? '–' : row.visits_per_user.toFixed(1)),
    },
    {
      key: 'total_visits',
      header: 'Total visits',
      sortable: true,
      align: 'end',
      nowrap: true,
      render: (row) => cell(row, row.total_visits),
    },
  ];
}

export interface PageUsageTableProps {
  pages: PageUsageRow[];
  totalUsers: number;
  onSelectPage: (route: TrackedPageRoute) => void;
}

/** One row per web app page: how many of the people who use the app found it. */
export function PageUsageTable({ pages, totalUsers, onSelectPage }: PageUsageTableProps) {
  const [sort, setSort] = useState<DataTableSort>({ key: 'users', direction: 'desc' });
  const [showRoutes, setShowRoutes] = useState(false);
  const [hideUnused, setHideUnused] = useState(true);

  const rows = useMemo<PageUsageTableRow[]>(
    () =>
      pages.map((page) => ({
        route: page.route,
        label: TRACKED_PAGES[page.route].label,
        section: TRACKED_PAGES[page.route].section,
        users: page.users,
        users_last_30_days: page.users_last_30_days,
        visits_per_user: page.users === 0 ? null : page.total_visits / page.users,
        total_visits: page.total_visits,
      })),
    [pages],
  );

  const used = useClientSort(
    rows.filter((row) => row.users > 0),
    sort,
  );
  const unused = rows.filter((row) => row.users === 0);

  return (
    <ChartCard
      title='Pages'
      description='Share of the people who opened the web app at least once. Click a page to see who used it.'
      aside={
        <div className='flex flex-wrap items-center gap-4'>
          <div className='flex items-center gap-2'>
            <span id='page-usage-hide-unused' className='text-sm text-ink-muted'>
              Hide unused pages
            </span>
            <Switch
              size='sm'
              aria-labelledby='page-usage-hide-unused'
              checked={hideUnused}
              onCheckedChange={setHideUnused}
            />
          </div>
          <div className='flex items-center gap-2'>
            <span id='page-usage-show-routes' className='text-sm text-ink-muted'>
              Show routes
            </span>
            <Switch
              size='sm'
              aria-labelledby='page-usage-show-routes'
              checked={showRoutes}
              onCheckedChange={setShowRoutes}
            />
          </div>
        </div>
      }
    >
      <DataTable<PageUsageTableRow>
        label='Page usage'
        columns={columns(totalUsers, showRoutes)}
        rows={hideUnused ? used : [...used, ...unused]}
        rowKey={(row) => row.route}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => onSelectPage(row.route)}
        minWidth='lg'
      />
    </ChartCard>
  );
}
